import { ChangeEvent, ReactNode, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Box,
  CircularProgress,
  Dialog,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  DeleteOutline,
  Download,
  Photo,
  PictureAsPdf,
  Videocam,
  Visibility,
} from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import {
  MAX_EMPLOYEE_ATTACHMENT_BYTES,
  MAX_EMPLOYEE_VIDEO_BYTES,
} from "../../../constants/uploadLimits";
import { resizeEmployeeAttachment } from "../../../utils/imageUtility";
import {
  clearEmployeeAttachment,
  selectProcesses,
  uploadPicture,
} from "../processSlices";
import { PhotoTypes } from "../types/processTypes";

export type AttachmentManageProps = {
  employeeId: string;
  photoType: PhotoTypes;
  enabled: boolean;
  /** File picker accept attribute; default `image/*` */
  accept?: string;
};

function formatFileSizeForUpload(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

/** Uploaded file URLs from the server typically end with `.pdf`. */
function isPdfAttachmentUrl(url?: string | null): boolean {
  if (!url) return false;
  const base = url.trim().toLowerCase().split("?")[0].split("#")[0];
  return base.endsWith(".pdf");
}

function isVideoAttachmentUrl(url?: string | null): boolean {
  if (!url) return false;
  const base = url.trim().toLowerCase().split("?")[0].split("#")[0];
  return /\.(mp4|webm|mov|m4v|ogg)$/.test(base);
}

const PREVIEW_LAYOUT = {
  default: { areaHeight: 260, width: 320, height: 260, pdfIcon: 72 },
  compact: { areaHeight: 128, width: 120, height: 120, pdfIcon: 48 },
} as const;

export const EmployeeMediaCard = ({
  title,
  src,
  icon,
  compact = false,
  mediaKind = "image",
  attachmentManage,
  uploadInputIdPrefix = "employee-media-upload",
}: {
  title: string;
  src?: string;
  icon: ReactNode;
  compact?: boolean;
  mediaKind?: "image" | "video";
  attachmentManage?: AttachmentManageProps;
  /** Prefix for hidden file input id (avoids collisions when multiple cards mount). */
  uploadInputIdPrefix?: string;
}) => {
  const isVideo =
    mediaKind === "video" || (Boolean(src) && isVideoAttachmentUrl(src));
  const hasMedia = Boolean(src);
  const isPdf = useMemo(() => isPdfAttachmentUrl(src), [src]);
  const preview = compact ? PREVIEW_LAYOUT.compact : PREVIEW_LAYOUT.default;
  const maxBytes = isVideo
    ? MAX_EMPLOYEE_VIDEO_BYTES
    : MAX_EMPLOYEE_ATTACHMENT_BYTES;
  const maxSizeLabel = isVideo ? "50 MB" : "5 MB";

  const [openPreview, setOpenPreview] = useState(false);
  const [uploadSizeError, setUploadSizeError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const previewVideoRef = useRef<HTMLVideoElement>(null);

  const dispatch = useAppDispatch();
  const { loading } = useAppSelector(selectProcesses);
  const busy = loading === "pending";

  const inputId = attachmentManage?.enabled
    ? `${uploadInputIdPrefix}-${attachmentManage.photoType}-${attachmentManage.employeeId}`
    : "";

  const handlePickFile = async (event: ChangeEvent<HTMLInputElement>) => {
    if (!attachmentManage?.enabled) return;
    const raw = event.target.files?.[0];
    event.target.value = "";
    setUploadSizeError(null);
    if (!raw) return;

    if (raw.size > maxBytes) {
      setUploadSizeError(
        `This file is ${formatFileSizeForUpload(raw.size)}. Maximum size is ${maxSizeLabel}.`,
      );
      return;
    }

    const fileToUpload = isVideo
      ? raw
      : await resizeEmployeeAttachment(raw);

    if (fileToUpload.size > maxBytes) {
      setUploadSizeError(
        `After processing, the file is ${formatFileSizeForUpload(fileToUpload.size)} (max ${maxSizeLabel}). Try a smaller file.`,
      );
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    try {
      const result = await dispatch(
        uploadPicture({
          _id: attachmentManage.employeeId,
          type: attachmentManage.photoType,
          avatar: fileToUpload,
          onProgress: (percent) => setUploadProgress(percent),
        }),
      );

      if (uploadPicture.fulfilled.match(result) && isVideo) {
        setOpenPreview(true);
      }
    } finally {
      setIsUploading(false);
      setUploadProgress(null);
    }
  };

  const openFilePicker = () => {
    if (!attachmentManage?.enabled || busy) return;
    setUploadSizeError(null);
    fileInputRef.current?.click();
  };

  const handleRemoveAttachment = () => {
    if (!attachmentManage?.enabled || !hasMedia) return;
    setUploadSizeError(null);
    dispatch(
      clearEmployeeAttachment({
        employeeId: attachmentManage.employeeId,
        type: attachmentManage.photoType,
      }),
    );
  };

  const handleView = () => {
    if (!src) return;
    if (isPdf) {
      window.open(src, "_blank", "noopener,noreferrer");
      return;
    }
    setOpenPreview(true);
  };

  const handleDownload = () => {
    if (!src) return;
    const filenameBase = title.toLowerCase().replace(/\s+/g, "-");

    const triggerDownload = (href: string, filename: string) => {
      const link = document.createElement("a");
      link.href = href;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    fetch(src)
      .then((response) => {
        if (!response.ok) throw new Error("File fetch failed");
        return response.blob();
      })
      .then((blob) => {
        const extension = blob.type?.split("/")[1] || (isVideo ? "mp4" : "jpg");
        const objectUrl = URL.createObjectURL(blob);
        triggerDownload(objectUrl, `${filenameBase}.${extension}`);
        setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      })
      .catch(() => {
        window.open(src, "_blank", "noopener,noreferrer");
      });
  };

  const viewTooltip = !hasMedia
    ? "No file available"
    : isPdf
      ? "Open PDF"
      : isVideo
        ? "Play video"
        : "View image";

  const downloadTooltip = !hasMedia
    ? "No file available"
    : isPdf
      ? "Download PDF"
      : isVideo
        ? "Download video"
        : "Download image";

  const uploadTooltip = hasMedia
    ? `Replace attachment (max ${maxSizeLabel})`
    : `Add attachment (max ${maxSizeLabel})`;

  const renderPreviewContent = () => {
    if (isPdf && hasMedia) {
      return (
        <Paper
          variant="outlined"
          sx={{
            width: preview.width,
            height: preview.height,
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: compact ? 0.5 : 1,
            borderRadius: 2,
            bgcolor: "grey.50",
            borderStyle: "dashed",
            borderColor: "divider",
            overflow: "hidden",
          }}
        >
          <PictureAsPdf color="error" sx={{ fontSize: preview.pdfIcon }} />
          <Typography
            variant={compact ? "caption" : "body2"}
            color="text.secondary"
            fontWeight={600}
            textAlign="center"
            sx={{ px: 0.5, lineHeight: 1.2 }}
          >
            PDF uploaded
          </Typography>
          {!compact && (
            <Typography
              variant="caption"
              color="primary"
              sx={{
                cursor: "pointer",
                textDecoration: "underline",
                "&:hover": { opacity: 0.85 },
              }}
              onClick={() => window.open(src, "_blank", "noopener,noreferrer")}
            >
              Open in new tab
            </Typography>
          )}
        </Paper>
      );
    }

    if (isVideo && hasMedia) {
      return (
        <Box
          component="video"
          src={src}
          controls
          playsInline
          sx={{
            width: preview.width,
            height: preview.height,
            maxWidth: preview.width,
            maxHeight: preview.height,
            flexShrink: 0,
            borderRadius: 2,
            bgcolor: "common.black",
            objectFit: "contain",
          }}
        />
      );
    }

    return (
      <Avatar
        key={src || "none"}
        variant="rounded"
        src={src || ""}
        alt={title}
        sx={{
          width: preview.width,
          height: preview.height,
          maxWidth: preview.width,
          maxHeight: preview.height,
          flexShrink: 0,
          borderRadius: 2,
          bgcolor: "grey.100",
          "& .MuiAvatar-img": {
            objectFit: "contain",
            bgcolor: "common.white",
          },
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {isVideo ? "No video" : "No image"}
        </Typography>
      </Avatar>
    );
  };

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.25,
            display: "flex",
            alignItems: "center",
            gap: 1,
            bgcolor: "action.hover",
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            {icon}
            <Typography variant="subtitle2">{title}</Typography>
          </Stack>
        </Box>
        <Box
          sx={{
            position: "relative",
            px: compact ? 1.5 : 2,
            py: compact ? 1 : 2,
            height: preview.areaHeight,
            minHeight: preview.areaHeight,
            maxHeight: preview.areaHeight,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "background.default",
            overflow: "hidden",
            boxSizing: "border-box",
          }}
        >
          {renderPreviewContent()}
          {isUploading && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                bgcolor: "rgba(255, 255, 255, 0.88)",
                zIndex: 2,
              }}
            >
              <Box sx={{ position: "relative", display: "inline-flex" }}>
                <CircularProgress
                  variant={
                    uploadProgress != null ? "determinate" : "indeterminate"
                  }
                  value={uploadProgress ?? undefined}
                  size={compact ? 44 : 56}
                />
                {uploadProgress != null && (
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      color="text.secondary"
                    >
                      {`${uploadProgress}%`}
                    </Typography>
                  </Box>
                )}
              </Box>
              <Typography variant="caption" color="text.secondary">
                {uploadProgress != null && uploadProgress < 100
                  ? "Uploading…"
                  : "Processing…"}
              </Typography>
            </Box>
          )}
        </Box>
        {uploadSizeError && (
          <Typography
            variant="caption"
            component="div"
            role="alert"
            color="error"
            sx={{ px: 2, pt: 0.5, pb: 0, textAlign: "center" }}
          >
            {uploadSizeError}
          </Typography>
        )}
        <Box
          sx={{
            px: compact ? 1 : 1.5,
            py: compact ? 0.75 : 1.25,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: compact ? 0.25 : 0.5,
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: "grey.50",
          }}
        >
          <Tooltip title={viewTooltip}>
            <span>
              <IconButton
                size={compact ? "small" : "medium"}
                onClick={handleView}
                disabled={!hasMedia}
                sx={{
                  borderRadius: 1.5,
                  color: "text.secondary",
                  "&:hover:not(.Mui-disabled)": {
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                  },
                }}
              >
                <Visibility fontSize={compact ? "small" : "medium"} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={downloadTooltip}>
            <span>
              <IconButton
                size={compact ? "small" : "medium"}
                onClick={handleDownload}
                disabled={!hasMedia}
                sx={{
                  borderRadius: 1.5,
                  color: "text.secondary",
                  "&:hover:not(.Mui-disabled)": {
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                  },
                }}
              >
                <Download fontSize={compact ? "small" : "medium"} />
              </IconButton>
            </span>
          </Tooltip>
          {attachmentManage?.enabled && (
            <>
              <input
                ref={fileInputRef}
                hidden
                accept={attachmentManage.accept ?? "image/*"}
                id={inputId}
                type="file"
                onChange={handlePickFile}
              />
              <Tooltip title={uploadTooltip}>
                <span>
                  <IconButton
                    size={compact ? "small" : "medium"}
                    type="button"
                    onClick={openFilePicker}
                    disabled={busy}
                    aria-controls={inputId}
                    sx={{
                      borderRadius: 1.5,
                      color: "text.secondary",
                      "&:hover:not(.Mui-disabled)": {
                        bgcolor: "primary.main",
                        color: "primary.contrastText",
                      },
                    }}
                  >
                    {isVideo ? (
                      <Videocam fontSize={compact ? "small" : "medium"} />
                    ) : (
                      <Photo fontSize={compact ? "small" : "medium"} />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={hasMedia ? "Remove attachment" : "No attachment"}>
                <span>
                  <IconButton
                    size={compact ? "small" : "medium"}
                    onClick={handleRemoveAttachment}
                    disabled={!hasMedia || busy}
                    sx={{
                      borderRadius: 1.5,
                      color: "text.secondary",
                      "&:hover:not(.Mui-disabled)": {
                        bgcolor: "error.main",
                        color: "error.contrastText",
                      },
                    }}
                  >
                    <DeleteOutline fontSize={compact ? "small" : "medium"} />
                  </IconButton>
                </span>
              </Tooltip>
            </>
          )}
        </Box>
      </Paper>
      <Dialog
        open={openPreview}
        onClose={() => setOpenPreview(false)}
        maxWidth="md"
        TransitionProps={{
          onEntered: () => {
            previewVideoRef.current?.play().catch(() => undefined);
          },
        }}
      >
        <Box sx={{ p: 1.5, bgcolor: "background.default" }}>
          {isVideo && src ? (
            <Box
              component="video"
              ref={previewVideoRef}
              src={src}
              controls
              autoPlay
              playsInline
              sx={{
                width: { xs: "85vw", md: 720 },
                maxHeight: { xs: "70vh", md: 620 },
                borderRadius: 2,
                bgcolor: "common.black",
                objectFit: "contain",
                display: "block",
              }}
            />
          ) : (
            <Avatar
              variant="rounded"
              src={src || ""}
              alt={title}
              sx={{
                width: { xs: "85vw", md: 720 },
                height: { xs: "70vh", md: 620 },
                borderRadius: 2,
                bgcolor: "common.white",
                "& .MuiAvatar-img": {
                  objectFit: "contain",
                },
              }}
            />
          )}
        </Box>
      </Dialog>
    </>
  );
};
